import { useField } from "formik"
import elementTypes from "../Elements/elementTypes"
import FormikDropDown from "../../../../../helpers/formikInputs/FormikDropDown"
import FormikMultiSelectInput from "../../../../../helpers/formikInputs/FormikMultiSelectInput"
import FormikCalendar from "../../../../../helpers/formikInputs/FormikCalendar"
import { useEffect } from "react"
import { DateTime } from "luxon"
import {
  getAssessmentDateOnlyFromIso,
  getAssessmentDateOnlyISO,
  getAssessmentTimeOnlyFromIso,
  getAssessmentTimeOnlyISO,
} from "../../helpers/assessmentDateHelper"

export default function QuestionDefaultValue({
  formikQuestionName,
  questionType,
  deletedOptionId,
  onResetDeletedOptionId,
  disabled,
}) {
  const multiValueSplitter = "||"
  const tempDateFieldName = `${formikQuestionName}.tmp_defaultValue`
  const tempMultivalueFieldName = `${formikQuestionName}.defaultValues`

  const [options] = useField(`${formikQuestionName}.options`)
  const [field, , helpers] = useField(`${formikQuestionName}.defaultValue`)
  const [, , tempDateHelpers] = useField(tempDateFieldName)
  const [multiField, , multiHelpers] = useField(tempMultivalueFieldName)

  useEffect(() => {
    switch (questionType) {
      case elementTypes.checkboxElementId:
        if (!field.value) {
          multiHelpers.setValue([])
        } else {
          multiHelpers.setValue(field.value?.split(multiValueSplitter) || [])
        }
        break
      case elementTypes.dateElementId:
      case elementTypes.timeElementId:
        if (field.value) {
          const dateValue = DateTime.fromISO(field.value)
          if (field.value === "none") {
            multiHelpers.setValue("none")
          } else if (field.value === "current") {
            multiHelpers.setValue("current")
          } else if (dateValue.isValid) {
            multiHelpers.setValue("custom")

            const tempValue =
              elementTypes.dateElementId === questionType
                ? getAssessmentDateOnlyFromIso(field.value)
                : getAssessmentTimeOnlyFromIso(field.value)

            tempDateHelpers.setValue(tempValue.toJSDate())
          } else {
            multiHelpers.setValue("")
          }
        }

        break
      default:
        break
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (deletedOptionId && questionType === elementTypes.checkboxElementId) {
    let values = field.value?.split(multiValueSplitter) || []
    values = values.filter((value) => value !== deletedOptionId && value !== "")

    helpers.setValue(values.join(multiValueSplitter))
    multiHelpers.setValue(values)
    onResetDeletedOptionId()
  } else if (deletedOptionId === field.value) {
    helpers.setValue(null)
  }

  const updateMultiOptionValues = (selectedOptions) => {
    multiHelpers.setValue(selectedOptions)
    helpers.setValue(selectedOptions.join(multiValueSplitter))
  }

  let component = null

  switch (questionType) {
    case elementTypes.checkboxElementId:
      component = (
        <FormikMultiSelectInput
          name={tempMultivalueFieldName}
          label={"Default value"}
          options={options?.value?.filter((option) => !option.isDeleted) || []}
          optionLabel={"text"}
          optionValue={"id"}
          placeHolderValue={"No default value specified"}
          disableFormikSubmit={true}
          onChangeCallBack={(values) => {
            updateMultiOptionValues(values)
          }}
          disabled={disabled}
        />
      )
      break
    case elementTypes.dateElementId:
    case elementTypes.timeElementId:
      const isTime = questionType === elementTypes.timeElementId

      const calendarOptionsArray = [
        { label: "None", value: "" },
        { label: isTime ? "Current time" : "Current date", value: "current" },
        { label: "Custom", value: "custom" },
      ]

      component = (
        <>
          <FormikDropDown
            name={tempMultivalueFieldName}
            label={"Default value"}
            options={calendarOptionsArray}
            optionLabel={"label"}
            optionValue={"value"}
            placeHolderValue={"No default value specified"}
            onChangeCallback={(value) => {
              if (value !== "custom" && field.value !== value) {
                helpers.setValue(value)
              } else if (value === "custom" && field.value) {
                const currentValue = DateTime.fromISO(field.value)
                if (!currentValue.isValid) {
                  helpers.setValue(undefined)
                }
              }
            }}
            disabled={disabled}
          />

          <FormikCalendar
            name={tempDateFieldName}
            label={"Specify the custom default date"}
            helpText={"Select custom default date"}
            onChangeCallBack={(value) => {
              if (!isTime) {
                helpers.setValue(getAssessmentDateOnlyISO(value))
              } else {
                helpers.setValue(getAssessmentTimeOnlyISO(value))
              }
            }}
            showIcon
            iconPos="left"
            disabled={multiField.value !== "custom" || disabled}
            timeOnly={isTime}
            icon={
              isTime ? <span class="material-icons">schedule</span> : undefined
            }
          />
        </>
      )
      break

    default:
      component = (
        <FormikDropDown
          name={`${formikQuestionName}.defaultValue`}
          label={"Default value"}
          options={options.value.filter((option) => !option.isDeleted)}
          optionLabel={"text"}
          optionValue={"id"}
          placeHolderValue={"No default value specified"}
          disabled={disabled}
        />
      )
      break
  }

  return component
}
